import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { InferType, Schema } from 'yup';

interface RouteSchema {
  params?: Schema;
  query?: Schema;
}

type ValidatedRoute<S extends RouteSchema> = Omit<RouteLocationNormalizedLoaded, 'params' | 'query'> & {
  params: S['params'] extends Schema ? InferType<S['params']> : {};
  query: S['query'] extends Schema ? InferType<S['query']> : {};
};

export const useValidatedRoute = <S extends RouteSchema>(schema: S, error?: () => Error): ValidatedRoute<S> => {
  const route = useRoute();

  const params = computed(() => {
    try {
      return schema.params?.validateSync(route.params) ?? {};
    } catch {
      throw error?.() ?? createError({ statusCode: 404, message: 'Page Not Found' });
    }
  });

  const query = computed(() => {
    try {
      return schema.query?.validateSync(route.query) ?? {};
    } catch {
      throw error?.() ?? createError({ statusCode: 404, message: 'Page Not Found' });
    }
  });

  // возвращаем проксированный route с реактивными params/query
  return new Proxy(route, {
    get(target, prop, receiver) {
      if (prop === 'params') return params.value;
      if (prop === 'query') return query.value;
      return Reflect.get(target, prop, receiver);
    },
  }) as ValidatedRoute<S>;
};
